import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { FC, useState, useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { getAS2MessageEndpoint } from '../clients/HrpsCertificatesApi';
import { Heading, useTranslation } from '../components/blocks';
import { Text } from '../components/blocks/localization';
import { Tile, usePageMessaging, ProgressLoader } from '../components/composites';
import { RootState } from '../redux/types';
import { MessageEndpointDetails } from '../types/certificate';

const CertificateListPage: FC<CertificateListPageProps> = ({ username }) => {
    const { t } = useTranslation('certificateListPage');
    const { showError, showSuccess } = usePageMessaging();

    const copyToClipboard = useCallback(
        async (text: string, label: string) => {
            try {
                await navigator.clipboard.writeText(text);
                showSuccess(t(`${label} copied to clipboard`));
            } catch (error) {
                showError(t('Failed to copy to clipboard'));
            }
        },
        [showError, showSuccess, t]
    );

    const [endpoint, setEndpoint] = useState<MessageEndpointDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleGetEndpoint = useCallback(async () => {
        if (!username) return;

        setLoading(true);
        setHasError(false);
        try {
            const result = await getAS2MessageEndpoint({ endpointName: username });
            setEndpoint(result.endpoint);
        } catch (error: any) {
            const errorCode = error?.code;
            let errorMessage;

            switch (errorCode) {
                case 'AccessDenied':
                    errorMessage = t('certificateList-accessDeniedError');
                    break;
                case 'Dependency':
                    errorMessage = t('certificateList-dependencyError');
                    break;
                case 'InvalidInput':
                    errorMessage = t('certificateList-invalidInputError');
                    break;
                default:
                    errorMessage = t('certificateList-errorMessage');
            }

            showError(errorMessage);
            setHasError(true);
        } finally {
            setLoading(false);
        }
    }, [showError, t, username]);

    useEffect(() => {
        if (username) {
            handleGetEndpoint();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const isInbound = !!endpoint?.CurrentSettings?.ProtocolDetails?.AS2Inbound;
    const isOutbound = !!endpoint?.CurrentSettings?.ProtocolDetails?.AS2Outbound;

    return (
        <Column>
            <Row>
                <Heading level={4}>
                    <Text data-testid={'certificateListPageHeader'}>{t('certificateList-heading')}</Text>
                </Heading>
            </Row>
            <Row>
                <Tile title={t('certificateList-tileTitle')} width={'100%'}>
                    <Column>
                        {(loading || (!loading && !endpoint && !hasError)) && (
                            <ProgressLoader isLoading={loading} estimatedTime={3} onFinished={() => {}} />
                        )}
                        {endpoint && (
                            <Row>
                                <Column>
                                    <Text>
                                        <strong>{t('certificateList-endpointId')}</strong> {endpoint.EndpointId}
                                    </Text>
                                    <Text>
                                        <strong>{t('certificateList-name')}</strong> {endpoint.EndpointName}
                                    </Text>
                                    <Text>
                                        <strong>{t('certificateList-protocol')}</strong> {endpoint.Protocol}
                                    </Text>
                                    <Text>
                                        <strong>{t('certificateList-state')}</strong> {endpoint.EndpointState}
                                    </Text>
                                    <Text>
                                        <strong>{t('certificateList-direction')}</strong> {endpoint.Direction}
                                    </Text>
                                    <Text>
                                        <strong>{t('certificateList-description')}</strong> {endpoint.Description}
                                    </Text>

                                    {isInbound && endpoint.CurrentSettings?.ProtocolDetails?.AS2Inbound && (
                                        <>
                                            <Text>
                                                <strong>{t('certificateList-as2ServerName')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.AS2ServerName}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-receiverId')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.ReceiverId}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-senderId')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.SenderId}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-url')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.Url}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-encryptionAlgorithm')}</strong>{' '}
                                                {
                                                    endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                        .MessageEncryptionAlgorithm
                                                }
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-signingAlgorithm')}</strong>{' '}
                                                {
                                                    endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                        .MessageSigningAlgorithm
                                                }
                                            </Text>
                                            <Row>
                                                <Column width={'auto'}>
                                                    <Text>
                                                        <strong>{t('certificateList-encryptionCert')}</strong>{' '}
                                                        {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                            .EncryptionCertificate
                                                            ? `${endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.EncryptionCertificate.substring(
                                                                  0,
                                                                  25
                                                              )}...`
                                                            : t('certificateList-none')}
                                                    </Text>
                                                </Column>
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                    .EncryptionCertificate && (
                                                    <Column width={'auto'}>
                                                        <Button
                                                            size={'small'}
                                                            onClick={() =>
                                                                copyToClipboard(
                                                                    endpoint.CurrentSettings!.ProtocolDetails!
                                                                        .AS2Inbound!.EncryptionCertificate!,
                                                                    'Encryption Certificate'
                                                                )
                                                            }
                                                        >
                                                            {'Copy'}
                                                        </Button>
                                                    </Column>
                                                )}
                                            </Row>
                                            <Row>
                                                <Column width={'auto'}>
                                                    <Text>
                                                        <strong>{t('certificateList-verificationCert')}</strong>{' '}
                                                        {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                            .VerificationCertificate
                                                            ? `${endpoint.CurrentSettings.ProtocolDetails.AS2Inbound.VerificationCertificate.substring(
                                                                  0,
                                                                  25
                                                              )}...`
                                                            : t('certificateList-none')}
                                                    </Text>
                                                </Column>
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Inbound
                                                    .VerificationCertificate && (
                                                    <Column width={'auto'}>
                                                        <Button
                                                            size={'small'}
                                                            onClick={() =>
                                                                copyToClipboard(
                                                                    endpoint.CurrentSettings!.ProtocolDetails!
                                                                        .AS2Inbound!.VerificationCertificate!,
                                                                    'Verification Certificate'
                                                                )
                                                            }
                                                        >
                                                            {'Copy'}
                                                        </Button>
                                                    </Column>
                                                )}
                                            </Row>
                                        </>
                                    )}

                                    {isOutbound && endpoint.CurrentSettings?.ProtocolDetails?.AS2Outbound && (
                                        <>
                                            <Text>
                                                <strong>{t('certificateList-as2ServerName')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Outbound.AS2ServerName}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-senderId')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Outbound.SenderId}
                                            </Text>
                                            <Text>
                                                <strong>{t('certificateList-receiverId')}</strong>{' '}
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Outbound.ReceiverId}
                                            </Text>
                                            <Row>
                                                <Column width={'auto'}>
                                                    <Text>
                                                        <strong>{t('certificateList-verificationCert')}</strong>{' '}
                                                        {endpoint.CurrentSettings.ProtocolDetails.AS2Outbound
                                                            .VerificationCertificate
                                                            ? `${endpoint.CurrentSettings.ProtocolDetails.AS2Outbound.VerificationCertificate.substring(
                                                                  0,
                                                                  25
                                                              )}...`
                                                            : t('certificateList-none')}
                                                    </Text>
                                                </Column>
                                                {endpoint.CurrentSettings.ProtocolDetails.AS2Outbound
                                                    .VerificationCertificate && (
                                                    <Column width={'auto'}>
                                                        <Button
                                                            size={'small'}
                                                            onClick={() =>
                                                                copyToClipboard(
                                                                    endpoint.CurrentSettings!.ProtocolDetails!
                                                                        .AS2Outbound!.VerificationCertificate!,
                                                                    'Verification Certificate'
                                                                )
                                                            }
                                                        >
                                                            {'Copy'}
                                                        </Button>
                                                    </Column>
                                                )}
                                            </Row>
                                        </>
                                    )}
                                </Column>
                            </Row>
                        )}
                    </Column>
                </Tile>
            </Row>
        </Column>
    );
};

const mapStateToProps = ({ userReducer }: RootState) => {
    return {
        username: userReducer.user?.username,
    };
};

const connector = connect(mapStateToProps);
export type CertificateListPageProps = ConnectedProps<typeof connector>;
export default connector(CertificateListPage);
